import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useHeader } from "../context/HeaderContext.jsx";
import BrandCarousel from "../components/BrandCarousel.jsx";
import { fetchBrandList } from "../utils/brandList.js";
import { peekPendingBrandNav, requestBrandNav } from "../utils/brandNavSignal.js";

export default function FeaturedGatewayPage() {
  const { setLoading, setCollapsed } = useHeader();
  const location = useLocation();

  // Take the carousel to the first brand — Balenciaga, since fetchBrandList is
  // alphabetical. Used on a cold arrival (no brand chosen) and again whenever
  // the section's own nav link is pressed while already on the page, so
  // SHOWCASE always means the same place. This used to roll a random brand;
  // brands[0] rather than the name spelled out, so the landing follows the
  // live list instead of pointing at a brand that can sell out from under it.
  const goFirst = useCallback(async () => {
    const brands = await fetchBrandList();
    if (brands.length) requestBrandNav(brands[0].name);
  }, []);

  // The page opens ON the carousel — the list of brand names that used to sit
  // over it on first load is gone. A brand chosen on the way in is honoured by
  // BrandCarousel and skips this, so only a plain arrival lands on the first.
  // It can come two ways: location.state (a product's brand link) or a request
  // parked on the brand-nav channel (the sidebar's brand list, the header
  // brands menu), which the carousel takes once its rows exist. Checking only
  // the state sent every sidebar pick to the first brand instead: the parked
  // request landed, then goFirst's own request overrode it.
  // Read once, at mount (before the carousel takes the request), so the
  // carousel later clearing either cannot send an already-placed deck back to
  // the start.
  const arrivedWithBrand = useRef(
    Boolean(location.state?.targetBrand) || Boolean(peekPendingBrandNav()),
  );
  useEffect(() => {
    if (!arrivedWithBrand.current) goFirst();
  }, [goFirst]);

  // brand-index-open is dispatched by the header's SHOWCASE link pressed while
  // already on this page, and by the carousel's own SHOWCASE breadcrumb. There
  // is no index list to reopen any more, so both take the deck back to the
  // first brand.
  useEffect(() => {
    window.addEventListener("brand-index-open", goFirst);
    return () => window.removeEventListener("brand-index-open", goFirst);
  }, [goFirst]);

  // useLayoutEffect (not useEffect) so body.brands-page is applied synchronously
  // before the first paint. Otherwise the header renders one frame with its
  // non-brands background (opaque var(--bg)) before flipping to the
  // transparent brands-page vignette, producing a brief flash on route switch.
  useLayoutEffect(() => {
    setCollapsed(false);
    document.body.classList.add("brands-page");
    return () => {
      setLoading(false);
      setCollapsed(false);
      document.body.classList.remove("brands-page", "brands-header-collapsed", "brands-page-idle", "brands-on-footer");
    };
  }, [setLoading, setCollapsed]);

  // Idle collapse here is owned by BrandCarousel's rolling inactivity timer
  // (HEADER_IDLE_MS). A one-shot timer used to live here, but it fired 8s
  // after landing even while the user was actively using the page, which is
  // exactly what "keep the header up unless idle" rules out.

  const handleHeaderLoading = useCallback((loading) => {
    setLoading(loading);
  }, [setLoading]);

  const handleHeaderCollapsed = useCallback((collapsed) => {
    setCollapsed(collapsed);
  }, [setCollapsed]);

  return (
    <div className="app-shell featured-gateway-page" data-mode="CART">
      <main className="app-main featured-gateway-main">
        <BrandCarousel setHeaderLoading={handleHeaderLoading} setHeaderCollapsed={handleHeaderCollapsed} />
      </main>
    </div>
  );
}
